<script>
  import { createEventDispatcher } from 'svelte';

export let styling = {};
export let updateConfig;

const dispatch = createEventDispatcher();

let showVisualEditor = false;
let showJsonEditor = false;
let jsonInput = '';
let editingStyling = {};
let showAddRuleInput = false;
let newRuleName = '';
let showCustomPropertyInput = {};
let newCustomPropertyName = {};

// Initialize when component mounts or styling changes
$: if (styling && !showJsonEditor) {
  jsonInput = JSON.stringify(styling, null, 2);
  editingStyling = JSON.parse(JSON.stringify(styling));
}

function createStylingConfig() {
  const emptyStyling = {};
  editingStyling = emptyStyling;
  updateConfig('styling', emptyStyling);
  showJsonEditor = true;
}

function openVisualEditor() {
  editingStyling = JSON.parse(JSON.stringify(styling));
  showVisualEditor = true;
}

function openJsonEditor() {
  jsonInput = JSON.stringify(styling, null, 2);
  showJsonEditor = true;
}

function closeEditors() {
  showVisualEditor = false;
  showJsonEditor = false;
}

function saveStyles() {
  updateConfig('styling', editingStyling);
  closeEditors();
}

function saveJsonStyles() {
  try {
    const finalConfig = JSON.parse(jsonInput);
    if (typeof finalConfig !== 'object' || finalConfig === null) {
      alert('Invalid JSON: must be an object');
      return;
    }
    
    updateConfig('styling', finalConfig);
    closeEditors();
  } catch (e) {
    alert('Invalid JSON: ' + e.message);
  }
}

function addNewRule() {
  showAddRuleInput = true;
  newRuleName = '';
}

function confirmAddRule() {
  if (newRuleName && newRuleName.trim()) {
    editingStyling = {
      ...editingStyling,
      [newRuleName.trim()]: ''
    };
    showAddRuleInput = false;
    newRuleName = '';
  }
}

function cancelAddRule() {
  showAddRuleInput = false;
  newRuleName = '';
}

function deleteRule(selector) {
  if (confirm(`Delete style rule for "${selector}"?`)) {
    const newStyling = { ...editingStyling };
    delete newStyling[selector];
    editingStyling = newStyling;
  }
}

// Helper functions to determine rule types
function isLinkRule(selector) {
  return selector.startsWith('.link');
}

function isNodeRule(selector) {
  return selector.startsWith('.node');
}

// Parse CSS string into properties object
function parseCssString(cssString) {
  if (!cssString || typeof cssString !== 'string') return {};
  
  const properties = {};
  const declarations = cssString.split(';').filter(d => d.trim());
  
  declarations.forEach(declaration => {
    const [property, ...valueParts] = declaration.split(':');
    if (property && valueParts.length > 0) {
      properties[property.trim()] = valueParts.join(':').trim();
    }
  });
  
  return properties;
}

// Convert properties object back to CSS string
function propertiesToCssString(properties) {
  return Object.entries(properties)
    .map(([key, value]) => `${key}: ${value}`)
    .join(';\n') + (Object.keys(properties).length > 0 ? ';' : '');
}

// Update a specific property in a CSS rule
function updateRuleProperty(selector, property, value) {
  const currentCss = editingStyling[selector] || '';
  const properties = parseCssString(currentCss);
  properties[property] = value;
  editingStyling = {
    ...editingStyling,
    [selector]: propertiesToCssString(properties)
  };
}

// Delete a property from a CSS rule
function deleteRuleProperty(selector, property) {
  const currentCss = editingStyling[selector] || '';
  const properties = parseCssString(currentCss);
  delete properties[property];
  editingStyling = {
    ...editingStyling,
    [selector]: propertiesToCssString(properties)
  };
}

// HSL color conversion helpers
function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

function hslToHex(h, s, l) {
  s = s / 100;
  l = l / 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  
  if (0 <= h && h < 60) {
    r = c; g = x; b = 0;
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0;
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x;
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c;
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c;
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x;
  }
  
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);
  
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("");
}

function parseHslString(hslString) {
  if (!hslString || typeof hslString !== 'string') return null;
  
  const match = hslString.match(/hsl\((\d+)\s+(\d+)%\s+(\d+)%\s*\/\s*([\d.]+)\)/);
  if (match) {
    return {
      h: parseInt(match[1]),
      s: parseInt(match[2]),
      l: parseInt(match[3]),
      a: parseFloat(match[4])
    };
  }
  return null;
}

function formatHslString(h, s, l, a = 1) {
  return `hsl(${h} ${s}% ${l}% / ${a})`;
}
</script>

<div class="styling-config">
  <h4>Visual Styling</h4>
  
  {#if !styling || Object.keys(styling).length === 0}
    <div class="no-styling">
      <p>No visual styling configured</p>
      <button class="btn btn-primary" on:click={createStylingConfig}>
        + Create Styling Configuration
      </button>
    </div>
  {:else}
    <div class="styling-summary">
      <div class="summary-info">
        <p><strong>{Object.keys(styling).length}</strong> style rules configured</p>
      </div>
      <div class="editor-buttons">
        <button class="btn btn-primary" on:click={openVisualEditor}>
          Visual Editor
        </button>
        <button class="btn btn-secondary" on:click={openJsonEditor}>
          JSON Editor
        </button>
      </div>
    </div>
  {/if}
</div>

{#if showVisualEditor}
  <div class="modal-overlay" on:click={closeEditors}>
    <div class="modal-content large-modal" on:click|stopPropagation>
      <div class="modal-header">
        <h3>Visual Styling Editor</h3>
      </div>
      
      <div class="modal-body">
        <div class="editor-controls">
          {#if showAddRuleInput}
            <div class="add-rule-form">
              <div class="add-rule-input">
                <label>CSS Selector (e.g., .link.undirected > .main.type.typeName):</label>
                <input 
                  type="text" 
                  bind:value={newRuleName}
                  placeholder="Enter CSS selector..."
                  on:keydown={(e) => {
                    if (e.key === 'Enter') confirmAddRule();
                    if (e.key === 'Escape') cancelAddRule();
                  }}
                  autofocus
                />
              </div>
              <div class="add-rule-buttons">
                <button class="btn btn-primary" on:click={confirmAddRule}>
                  Add Rule
                </button>
                <button class="btn" on:click={cancelAddRule}>
                  Cancel
                </button>
              </div>
            </div>
          {:else}
            <button class="btn btn-primary" on:click={addNewRule}>
              + Add Style Rule
            </button>
          {/if}
        </div>
        
        <div class="rules-container">
          {#each Object.entries(editingStyling) as [selector, cssString]}
            {@const properties = parseCssString(cssString)}
            <div class="rule-section">
              <div class="rule-header">
                <h4>{selector}</h4>
                <button class="btn btn-danger" on:click={() => deleteRule(selector)}>
                  Delete Rule
                </button>
              </div>
              
              <div class="rule-content">
                <div class="property-controls">
                  <div class="properties-header">
                    <h5>Properties</h5>
                    <div class="add-property-controls">
                      <select class="property-type-select" on:change={(e) => {
                        if (e.target.value) {
                          const property = e.target.value;
                          const currentCss = editingStyling[selector] || '';
                          const properties = parseCssString(currentCss);
                          
                          if (property === 'custom') {
                            showCustomPropertyInput = { ...showCustomPropertyInput, [selector]: true };
                            newCustomPropertyName = { ...newCustomPropertyName, [selector]: '' };
                          } else {
                            // Set default values for common properties
                            let defaultValue = '';
                            if (property === 'opacity') defaultValue = '1';
                            else if (property === 'stroke-width') defaultValue = '1';
                            else if (property === 'r') defaultValue = '10px';
                            else if (property === 'stroke' || property === 'fill') defaultValue = 'hsl(0 0% 50% / 1.0)';
                            
                            if (!properties[property]) {
                              updateRuleProperty(selector, property, defaultValue);
                            }
                          }
                          e.target.value = '';
                        }
                      }}>
                        <option value="">+ Add Property</option>
                        <optgroup label="Common Link Properties" disabled={!isLinkRule(selector)}>
                          <option value="opacity" disabled={isLinkRule(selector) && properties.opacity !== undefined}>Opacity</option>
                          <option value="stroke-width" disabled={isLinkRule(selector) && properties['stroke-width'] !== undefined}>Stroke Width</option>
                          <option value="stroke" disabled={isLinkRule(selector) && properties.stroke !== undefined}>Stroke Color</option>
                        </optgroup>
                        <optgroup label="Common Node Properties" disabled={!isNodeRule(selector)}>
                          <option value="opacity" disabled={isNodeRule(selector) && properties.opacity !== undefined}>Opacity</option>
                          <option value="fill" disabled={isNodeRule(selector) && properties.fill !== undefined}>Fill Color</option>
                          <option value="r" disabled={isNodeRule(selector) && properties.r !== undefined}>Radius</option>
                          <option value="stroke-width" disabled={isNodeRule(selector) && properties['stroke-width'] !== undefined}>Stroke Width</option>
                          <option value="stroke" disabled={isNodeRule(selector) && properties.stroke !== undefined}>Stroke Color</option>
                        </optgroup>
                        <optgroup label="Custom Property">
                          <option value="custom">Custom Property...</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                  
                  <!-- Custom Property Input -->
                  {#if showCustomPropertyInput[selector]}
                    <div class="custom-property-input">
                      <input 
                        type="text" 
                        bind:value={newCustomPropertyName[selector]}
                        placeholder="Enter property name..."
                        on:keydown={(e) => {
                          if (e.key === 'Enter') {
                            if (newCustomPropertyName[selector] && newCustomPropertyName[selector].trim()) {
                              updateRuleProperty(selector, newCustomPropertyName[selector].trim(), '');
                              showCustomPropertyInput = { ...showCustomPropertyInput, [selector]: false };
                              newCustomPropertyName = { ...newCustomPropertyName, [selector]: '' };
                            }
                          }
                          if (e.key === 'Escape') {
                            showCustomPropertyInput = { ...showCustomPropertyInput, [selector]: false };
                            newCustomPropertyName = { ...newCustomPropertyName, [selector]: '' };
                          }
                        }}
                        autofocus
                      />
                      <button 
                        class="btn btn-primary btn-small" 
                        on:click={() => {
                          if (newCustomPropertyName[selector] && newCustomPropertyName[selector].trim()) {
                            updateRuleProperty(selector, newCustomPropertyName[selector].trim(), '');
                            showCustomPropertyInput = { ...showCustomPropertyInput, [selector]: false };
                            newCustomPropertyName = { ...newCustomPropertyName, [selector]: '' };
                          }
                        }}
                      >
                        Add
                      </button>
                      <button 
                        class="btn btn-small" 
                        on:click={() => {
                          showCustomPropertyInput = { ...showCustomPropertyInput, [selector]: false };
                          newCustomPropertyName = { ...newCustomPropertyName, [selector]: '' };
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  {/if}
                  
                  <!-- Dynamic Property Controls -->
                  {#each Object.entries(properties) as [property, value]}
                    <div class="property-control">
                      <div class="property-label">
                        <label>{property}:</label>
                      </div>
                      
                      <div class="property-input">
                        <!-- Opacity Control -->
                        {#if property === 'opacity'}
                          <div class="range-input-group">
                            <input 
                              type="range" 
                              min="0" 
                              max="1" 
                              step="0.01"
                              value={value || 1}
                              on:input={(e) => updateRuleProperty(selector, property, e.target.value)}
                            />
                            <input 
                              type="number" 
                              min="0" 
                              max="1" 
                              step="0.01"
                              value={value || 1}
                              on:input={(e) => updateRuleProperty(selector, property, e.target.value)}
                              class="range-number"
                            />
                          </div>
                        
                        <!-- Stroke Width Control -->
                        {:else if property === 'stroke-width'}
                          <input 
                            type="number" 
                            min="0" 
                            step="0.5"
                            value={value || 1}
                            on:input={(e) => updateRuleProperty(selector, property, e.target.value)}
                          />
                        
                        <!-- Radius Control -->
                        {:else if property === 'r'}
                          <div class="number-with-unit">
                            <input 
                              type="number" 
                              min="0" 
                              step="1"
                              value={value ? parseFloat(value.replace('px', '')) : 10}
                              on:input={(e) => updateRuleProperty(selector, property, e.target.value + 'px')}
                            />
                            <span class="unit">px</span>
                          </div>
                        
                        <!-- Color Controls (stroke, fill) -->
                        {:else if property === 'stroke' || property === 'fill'}
                          {@const hslData = parseHslString(value)}
                          {#if hslData}
                            <div class="color-input-group">
                              <input 
                                type="color"
                                value={hslToHex(hslData.h, hslData.s, hslData.l)}
                                on:input={(e) => {
                                  const hsl = hexToHsl(e.target.value);
                                  updateRuleProperty(selector, property, formatHslString(hsl.h, hsl.s, hsl.l, hslData.a));
                                }}
                              />
                              <input 
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={hslData.a}
                                on:input={(e) => {
                                  updateRuleProperty(selector, property, formatHslString(hslData.h, hslData.s, hslData.l, parseFloat(e.target.value)));
                                }}
                                title="Alpha"
                                class="alpha-slider"
                              />
                            </div>
                          {:else}
                            <div class="color-input-group">
                              <input 
                                type="color"
                                value={property === 'fill' ? "#4a90e2" : "#333333"}
                                on:input={(e) => {
                                  const hsl = hexToHsl(e.target.value);
                                  updateRuleProperty(selector, property, formatHslString(hsl.h, hsl.s, hsl.l, 1));
                                }}
                              />
                              <input 
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value="1"
                                on:input={(e) => {
                                  const currentHsl = parseHslString(properties[property]) || { h: 0, s: 0, l: 50 };
                                  updateRuleProperty(selector, property, formatHslString(currentHsl.h, currentHsl.s, currentHsl.l, parseFloat(e.target.value)));
                                }}
                                title="Alpha"
                                class="alpha-slider"
                              />
                            </div>
                          {/if}
                        
                        <!-- Generic Text Input -->
                        {:else}
                          <input 
                            type="text" 
                            value={value} 
                            on:input={(e) => updateRuleProperty(selector, property, e.target.value)}
                            class="text-input"
                          />
                        {/if}
                      </div>
                      <button 
                          class="btn btn-danger btn-tiny" 
                          on:click={() => deleteRuleProperty(selector, property)}
                          title="Delete property"
                        >
                          ×
                        </button>
                    </div>
                  {/each}
                </div>
                
                <!-- CSS Preview -->
                <div class="css-preview">
                  <h5>Generated CSS</h5>
                  <pre>{cssString || '/* No styles defined */'}</pre>
                </div>
              </div>
            </div>
          {/each}
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="btn" on:click={closeEditors}>Cancel</button>
        <button class="btn btn-primary" on:click={saveStyles}>Save Styling</button>
      </div>
    </div>
  </div>
{/if}

{#if showJsonEditor}
  <div class="modal-overlay" on:click={closeEditors}>
    <div class="modal-content large-modal" on:click|stopPropagation>
      <div class="modal-header">
        <h3>JSON Styling Editor</h3>
      </div>
      
      <div class="modal-body">
        <div class="json-editor">
          <div class="json-input-area">
            <label>Paste or edit JSON configuration:</label>
            <textarea 
              bind:value={jsonInput}
              rows="20"
            ></textarea>
          </div>
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="btn" on:click={closeEditors}>Cancel</button>
        <button class="btn btn-primary" on:click={saveJsonStyles}>Save JSON</button>
      </div>
    </div>
  </div>
{/if}
<style lang="scss">
  .styling-config {
  margin-bottom: 15px;
  
  h4 {
    margin-bottom: 10px;
    font-size: 0.85rem;
    font-weight: bold;
  }
}

.no-styling {
  text-align: center;
  padding: 20px;
  background: #f3f4f6;
  border-radius: 8px;
  border: 2px dashed #d1d5db;
  
  p {
    margin-bottom: 10px;
    color: #6b7280;
    font-style: italic;
  }
}

.styling-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background: #f0f9ff;
  border-radius: 8px;
  border: 1px solid #0ea5e9;
  
  .summary-info {
    flex: 1;
    
    p {
      margin: 0;
      font-size: 0.75rem;
      color: #0369a1;
    }
  }
  
  .editor-buttons {
    display: flex;
    gap: 8px;
    flex-direction: column;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
}

.modal-content.large-modal {
  background: #f6f6f6;
  border-radius: 10px;
  width: 95%;
  max-width: 1000px;
  height: 85%;
  border: 4px solid #d4d3bc;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px;
  border-bottom: 2px solid #d4d3bc;
  
  h3 {
    margin: 0;
  }
}

.modal-body {
  flex: 1;
  padding: 20px;
  overflow: auto;
}

.editor-controls {
  margin-bottom: 20px;
  text-align: center;
}

.add-rule-form {
  background: white;
  border: 2px solid #d4d3bc;
  border-radius: 8px;
  padding: 20px;
  max-width: 500px;
  margin: 0 auto;
  
  .add-rule-input {
    margin-bottom: 15px;
    
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 0.85rem;
      text-align: left;
    }
    
    input {
      width: 100%;
      padding: 10px;
      border: 2px solid #d1d5db;
      border-radius: 4px;
      font-size: 0.85rem;
      font-family: monospace;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
      }
    }
  }
  
  .add-rule-buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
  }
}

.rules-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rule-section {
  background: white;
  border: 2px solid #d4d3bc;
  border-radius: 8px;
  overflow: hidden;
}

.rule-header {
  background: #e3e2d3;
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #d4d3bc;
  
  h4 {
    margin: 0;
    font-family: monospace;
    font-size: 0.9rem;
  }
}

.rule-content {
  padding: 20px;
}

.property-controls {
  .properties-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h5 {
      margin: 0;
      color: #374151;
      font-size: 0.85rem;
    }
    
    .property-type-select {
      padding: 6px 8px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      font-size: 0.8rem;
      background: white;
      cursor: pointer;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
      }
      
      optgroup {
        font-weight: bold;
        
        &[disabled] {
          color: #9ca3af;
        }
      }
      
      option[disabled] {
        color: #9ca3af;
      }
    }
  }
}

.custom-property-input {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  margin-bottom: 15px;
  
  input {
    flex: 1;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 0.8rem;
    font-family: monospace;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
    }
  }
}

.property-control {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
  
  .property-label {
    min-width: 120px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    label {
      font-size: 0.8rem;
      font-weight: 500;
      color: #374151;
      font-family: monospace;
    }
    
    .btn-tiny {
      padding: 2px 6px;
      font-size: 0.7rem;
      min-width: auto;
    }
  }
  
  .property-input {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.range-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  
  input[type="range"] {
    flex: 1;
  }
  
  .range-number {
    width: 80px;
  }
}

.color-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
  
  input[type="color"] {
    width: 50px;
    height: 35px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 0;
    cursor: pointer;
  }
  
  .alpha-slider {
    width: 100px;
  }
}

.number-with-unit {
  display: flex;
  align-items: center;
  gap: 5px;
  
  input {
    width: 80px;
  }
  
  .unit {
    font-size: 0.75rem;
    color: #6b7280;
    font-weight: 500;
  }
}

.text-input {
  flex: 1;
  font-family: monospace;
  font-size: 0.8rem;
}

.css-preview {
  h5 {
    margin-bottom: 10px;
    color: #374151;
    font-size: 0.85rem;
  }
  
  pre {
    background: #f3f4f6;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 10px;
    font-size: 0.75rem;
    color: #1f2937;
    white-space: pre-wrap;
    max-height: 150px;
    overflow: auto;
  }
}

.json-editor {
  height: 100%;
}

.json-input-area {
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  
  label {
    font-weight: bold;
    font-size: 0.75rem;
  }
  
  textarea {
    flex: 1;
    padding: 12px;
    border: 2px solid #d4d3bc;
    border-radius: 8px;
    background: white;
    font-family: monospace;
    font-size: 0.75rem;
    resize: none;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
    }
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 2px solid #d4d3bc;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: bold;
  
  &.btn-primary {
    background: #3b82f6;
    color: white;
    text-wrap: inherit;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &.btn-secondary {
    background: #10b981;
    color: white;
    
    &:hover {
      background: #059669;
    }
  }
  
  &.btn-danger {
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
  }
  
  &.btn-small {
    padding: 4px 8px;
    font-size: 0.7rem;
  }
  
  &.btn-tiny {
    padding: 2px 6px;
    font-size: 0.65rem;
  }
  
  &:not(.btn-primary):not(.btn-secondary):not(.btn-danger) {
    background: #e5e7eb;
    color: #374151;
    
    &:hover {
      background: #d1d5db;
    }
  }
}

input[type="text"], input[type="number"] {
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.8rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
}
</style>